import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { searchBurgersByName, searchProducersByName, searchProfilesPublicByName } from "@/data/raw";
import { useNavigate } from "react-router-dom";
import { trackSearch } from "@/lib/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { getProfileUrl } from "@/lib/profileUrl";
import { transformedImageUrl } from "@/lib/imageTransform";

type BurgerRow = { id: string; slug: string; name: string; producer_id: string | null; canonical_image_url: string | null };
type ProducerRow = { id: string; slug: string; name: string; logo_url?: string | null };
type ProfileRow = { id: string; display_name: string | null; avatar_url: string | null; is_public: boolean | null; username?: string | null };

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SearchDialog: React.FC<SearchDialogProps> = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [burgers, setBurgers] = useState<BurgerRow[]>([]);
  const [producers, setProducers] = useState<ProducerRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setBurgers([]);
      setProducers([]);
      setProfiles([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const q = query.trim();
      if (!q) {
        setBurgers([]);
        setProducers([]);
        return;
      }
      try {
        setLoading(true);
        const [{ data: burgerRows }, { data: producerRows }, { data: profileRows }] = await Promise.all([
          searchBurgersByName(q),
          searchProducersByName(q),
          searchProfilesPublicByName(q),
        ]);
        setBurgers((burgerRows ?? []) as any);
        setProducers((producerRows ?? []) as any);
        setProfiles((profileRows ?? []) as any);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  const hasResults = (burgers?.length ?? 0) + (producers?.length ?? 0) + (profiles?.length ?? 0) > 0;

  const onBurgerClick = (burger: BurgerRow) => {
    onOpenChange(false);
    navigate(`/burger/${burger.slug}`);
  };
  const onProducerClick = (producer: ProducerRow) => {
    onOpenChange(false);
    navigate(`/producer/${producer.slug}`);
  };
  const onProfileClick = (profile: ProfileRow) => {
    onOpenChange(false);
    navigate(getProfileUrl(profile.id, profile.username));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Search</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col min-h-0 flex-1">
          <div className="flex-shrink-0 pb-4">
            <Input
              autoFocus
              placeholder="Search burgers, producers, or users..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (e.target.value.trim()) {
                  trackSearch(e.target.value.trim());
                }
              }}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}

            {!loading && !hasResults && query.trim() && (
              <p className="text-sm text-muted-foreground">No results found.</p>
            )}

            {!loading && hasResults && (
              <div className="space-y-3 pr-2">
                {burgers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Burgers</p>
                    <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                      {burgers.map((b) => (
                        <li key={b.id}>
                          <button
                            type="button"
                            className="w-full flex items-center gap-3 p-3 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 text-left"
                            onClick={() => onBurgerClick(b)}
                          >
                            <img
                              src={b.canonical_image_url ? transformedImageUrl(b.canonical_image_url, { width: 40, height: 40, quality: 70 }) : "/placeholder.svg"}
                              alt={b.name}
                              width={40}
                              height={40}
                              className="h-10 w-10 rounded object-cover bg-muted"
                              loading="lazy"
                              decoding="async"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{b.name}</p>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {producers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Producers</p>
                    <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                      {producers.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            className="w-full flex items-center gap-3 p-3 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 text-left"
                            onClick={() => onProducerClick(p)}
                          >
                            <img
                              src={p.logo_url ? transformedImageUrl(p.logo_url, { width: 40, height: 40, quality: 70 }) : "/placeholder.svg"}
                              alt={p.name}
                              width={40}
                              height={40}
                              className="h-10 w-10 rounded-full object-cover bg-muted"
                              loading="lazy"
                              decoding="async"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{p.name}</p>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {profiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">People</p>
                    <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                      {profiles.map((u) => (
                        <li key={u.id}>
                          <button
                            type="button"
                            className="w-full flex items-center gap-3 p-3 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 text-left"
                            onClick={() => onProfileClick(u)}
                          >
                            <img
                              src={u.avatar_url ? transformedImageUrl(u.avatar_url, { width: 40, height: 40, quality: 70 }) : "/placeholder.svg"}
                              alt={u.display_name ?? "User avatar"}
                              width={40}
                              height={40}
                              className="h-10 w-10 rounded-full object-cover bg-muted"
                              loading="lazy"
                              decoding="async"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{u.display_name || "User"}</p>
                              {u.username && (
                                <p className="text-xs text-muted-foreground truncate">@{u.username}</p>
                              )}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
